/*
 * Parser for the forecast text returned by POST /api/forecast.
 *
 * The endpoint returns `{forecast: "<text>"}` — a single AI-generated string in
 * a fixed layout, not structured fields. The split bar, predicted winner and
 * confidence badge all need numbers, so we extract them here.
 *
 * The text looks like this (see the prompt in utils/forecast.py):
 *
 *   ⚽ Soccer — Man City vs Arsenal
 *   📅 2026-07-29
 *   🕐 20:00 GMT
 *
 *   ⚖️ Win probabilities:
 *   Man City 64%
 *   Arsenal 36%
 *
 *   📊 Analysis:
 *   ...
 *
 *   🎯 SPORT-SPECIFIC INSIGHTS
 *   • ...
 *
 *   ⚠️ Disclaimer:
 *   ...
 *
 * Section headings are translated per user language, so we anchor on the emoji
 * markers, which are the same in every language, and never on English words.
 *
 * Parsing AI output is inherently fragile. Every caller must handle
 * `probabilities === null` by falling back to showing `raw` verbatim — a
 * forecast the user paid for must never be swallowed by a parse failure.
 */

const MARKERS = {
  probabilities: '⚖️',
  analysis: '📊',
  insights: '🎯',
  disclaimer: '⚠️',
};

/** Text from `marker` up to whichever of `nextMarkers` appears first. */
function section(text, marker, nextMarkers) {
  const start = text.indexOf(marker);
  if (start === -1) return '';

  let end = text.length;
  for (const next of nextMarkers) {
    const at = text.indexOf(next, start + marker.length);
    if (at !== -1 && at < end) end = at;
  }

  const body = text.slice(start + marker.length, end).trim();

  // Drop the section's own heading so it isn't repeated under the label the UI
  // already renders. Two forms appear: "Analysis:" / "Анализ:" (ends with a
  // colon) and "SPORT-SPECIFIC INSIGHTS" (upper case, no colon).
  const [first, ...rest] = body.split('\n');
  const headingWithColon = /^[^:]{0,48}:\s*$/.test(first.trim());
  const upperCaseHeading =
    first.trim().length <= 48 && first.trim() === first.trim().toUpperCase() && /\p{L}/u.test(first);

  if (headingWithColon || upperCaseHeading) return rest.join('\n').trim();

  // "Analysis: text on the same line" — strip just the label.
  return body.replace(/^[^\n:]{0,48}:\s*/, '').trim();
}

/**
 * Pull the two "Team NN%" lines out of the probabilities block.
 * Returns [{team, pct}, …] in the order they appear.
 */
function parseProbabilityLines(block) {
  const found = [];
  for (const line of block.split('\n')) {
    // Team names can contain digits ("Bayer 04"), so anchor the percentage to
    // the end of the line rather than taking the first number we see.
    const match = line.trim().match(/^(.*?)[\s:]*(\d{1,3}(?:[.,]\d+)?)\s*%$/);
    if (!match) continue;
    const team = match[1].trim();
    const pct = Number(match[2].replace(',', '.'));
    if (team && Number.isFinite(pct)) found.push({ team, pct });
  }
  return found;
}

/** Loose match between a parsed team name and the one we sent to the API. */
function isSameTeam(a = '', b = '') {
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9Ѐ-ӿ]/g, '');
  const x = norm(a);
  const y = norm(b);
  if (!x || !y) return false;
  return x === y || x.includes(y) || y.includes(x);
}

/**
 * Confidence rating, derived from the winning probability.
 *
 * The backend does not return a confidence level, so it's computed here — but
 * the thresholds are product semantics, not a display choice:
 *   70% is the Top Pick threshold, 60% is the VIP channel floor.
 * Keep them in step with the backend if either of those moves.
 */
export const CONFIDENCE_THRESHOLDS = { high: 70, med: 60 };

export function confidenceFor(winningPct) {
  if (winningPct >= CONFIDENCE_THRESHOLDS.high) return 'high';
  if (winningPct >= CONFIDENCE_THRESHOLDS.med) return 'med';
  return 'low';
}

/**
 * @param {string} raw       the `forecast` string from the API
 * @param {object} [match]   {home, away} as sent to the API, used to decide
 *                           which parsed percentage belongs to which side
 */
export function parseForecast(raw, match = {}) {
  const text = String(raw ?? '').trim();
  const result = {
    raw: text,
    probabilities: null,
    analysis: '',
    insights: '',
    disclaimer: '',
  };

  if (!text) return result;

  result.analysis = section(text, MARKERS.analysis, [MARKERS.insights, MARKERS.disclaimer]);
  result.insights = section(text, MARKERS.insights, [MARKERS.disclaimer]);
  result.disclaimer = section(text, MARKERS.disclaimer, []);

  const block = section(text, MARKERS.probabilities, [
    MARKERS.analysis,
    MARKERS.insights,
    MARKERS.disclaimer,
  ]);

  const lines = parseProbabilityLines(block);
  if (lines.length < 2) return result;

  // Match by name where we can; fall back to document order.
  let home = lines.find((l) => isSameTeam(l.team, match.home));
  let away = lines.find((l) => isSameTeam(l.team, match.away));
  if (!home || !away || home === away) {
    [home, away] = lines;
  }

  // The prompt requires the two to sum to 100; if the model drifted badly,
  // don't render a bar that misrepresents the numbers.
  if (Math.abs(home.pct + away.pct - 100) > 2) return result;

  // Round once and derive the other side, so a pair like 62.5/37.5 shows as
  // 63/37 rather than 63/38.
  const homePct = Math.round(home.pct);
  const awayPct = 100 - homePct;

  const homeWins = homePct >= awayPct;
  result.probabilities = {
    homeTeam: match.home || home.team,
    awayTeam: match.away || away.team,
    homeProbability: homePct,
    awayProbability: awayPct,
    winner: homeWins ? 'home' : 'away',
    predictedWinner: homeWins ? match.home || home.team : match.away || away.team,
    confidence: confidenceFor(Math.max(homePct, awayPct)),
  };

  return result;
}

/**
 * Top Picks and history rows also carry a `forecast_text`. This pulls just the
 * analysis paragraph out for previews.
 */
export function forecastSummary(raw) {
  return section(String(raw ?? ''), MARKERS.analysis, [MARKERS.insights, MARKERS.disclaimer]);
}
